import { config } from '../config.ts';
import { speakAttempt } from '../lib/coach.ts';
import { describeResult, prettyHeard, scoresForDisplayWords, wordTones } from '../lib/display.ts';
import { makeAttemptRecord } from '../lib/attemptLog.ts';
import { isRecordedPhase } from '../lib/recordings.ts';
import type { AttemptLabel } from '../lib/attemptLog.ts';
import { attemptPercent, sentencePercent } from '../lib/percent.ts';
import { wordKey } from '../lib/pronunciation.ts';
import { decide, reasonIn } from '../lib/scoring.ts';
import { feedbackText, heardClips, heardLine, partsClips, pickPraise, reasonParts } from '../lib/feedbackVoice.ts';
import type { FeedbackClip, FeedbackKey, FeedbackLang } from '../lib/feedbackVoice.ts';
import { planCourseWords, planDictionaryWords } from '../speech/audioPlan.ts';
import type { AzureRawPair } from '../speech/types.ts';
import { runAssessment, selfResult } from '../speech/assessors.ts';
import { AssessError } from '../speech/types.ts';
import type { AssessmentResult, SpeakTask, TeacherLang, Verdict } from '../types.ts';
import type { TaskEnv, TaskView } from './ctx.ts';
import { button, h } from './dom.ts';
import { dialogueLine } from './dialogue.ts';
import type { LineView } from './dialogue.ts';
import { say } from './forms.ts';
import { langOf, tx, txAttr, txText } from './i18n.ts';
import type { Pair } from './i18n.ts';
import { fitProps, fitToWindow, icon, labelFor, lessonPicture, mascot, sceneFitProps, starsEl, stepPose } from './kit.ts';
import type { Pose } from './kit.ts';
import { L } from './taskText.ts';
import { dismissOnOutsideTap, renderWordHelp } from './wordHelp.ts';
import { courseMeaning } from '../lib/courseWords.ts';
import { reactionPose } from '../lib/reaction.ts';

// Napisy tego ekranu (patch-33): idą za przełącznikiem EN/PL. Wspólne przyciski (Posłuchaj, Wolno, Dalej…) są w taskText.ts.
const T = {
  phase: {
    repeat: { en: 'Repeat after me', pl: 'Powtórz po mnie' },
    read: { en: 'Read out loud', pl: 'Przeczytaj na głos' },
    build: { en: 'Say the sentence', pl: 'Powiedz zdanie' },
    scene: { en: 'Your line', pl: 'Twoja kwestia' },
  },
  tapWord: { en: 'Tap to see how to read it', pl: 'Dotknij, żeby zobaczyć, jak to czytać' },
  speak: { en: 'Speak', pl: 'Mów' },
  selfOk: { en: '👍 I did well', pl: '👍 Dobrze mi poszło' },
  selfRetry: { en: '🔁 Try again', pl: '🔁 Jeszcze raz' },
  skip: { en: 'Skip', pl: 'Pomiń' },
  technical: { en: 'Technical details: ', pl: 'Szczegóły techniczne: ' },
} as const satisfies Record<string, Pair | Record<string, Pair>>;

/** `result.note` (speech/assessors.ts) jest po polsku i zawiera techniczną listę przyczyn: po polsku pokazujemy ją w całości, po angielsku samo sedno (pełny tekst zostaje w konsoli). */
const backupNote = (note: string, lang: TeacherLang): string => (lang === 'en' ? 'The first way of checking did not work, so a backup way was used.' : note);

/** Co się stało z mikrofonem albo rozpoznawaniem: krótka uwaga bota (czytana głosem) i dłuższe wyjaśnienie dla rodzica (drobnym drukiem). */
const ERROR_FEEDBACK: Record<string, { say: FeedbackKey; parent: Pair }> = {
  'mic-denied': { say: 'micOff', parent: { en: 'The browser does not have permission to use the microphone. Click the icon next to the page address → Microphone → Allow, and try again. For now your child can rate themselves.', pl: 'Przeglądarka nie ma zgody na mikrofon. Kliknij ikonę przy adresie strony → Mikrofon → Zezwól, i spróbuj jeszcze raz. Na razie dziecko może ocenić się samo.' } },
  'no-mic': { say: 'micOff', parent: { en: 'The browser could not start the microphone. Check that the microphone is plugged in, chosen in the system settings and not used by another app (Teams, Zoom). Try again, or let your child rate themselves.', pl: 'Przeglądarka nie uruchomiła mikrofonu. Sprawdź, czy mikrofon jest podłączony, wybrany w systemie i nieużywany przez inną aplikację (Teams, Zoom). Spróbuj jeszcze raz albo niech dziecko oceni się samo.' } },
  network: { say: 'network', parent: { en: 'The connection to the speech recognition service failed (this does not count as an attempt). Try again; if it keeps happening, try Chrome or Edge, or let your child rate themselves.', pl: 'Połączenie z usługą rozpoznawania mowy zawiodło (to nie liczy się jako podejście). Spróbuj jeszcze raz; jeśli się powtarza, spróbuj w Chrome lub Edge, albo niech dziecko oceni się samo.' } },
  fallback: { say: 'noCheck', parent: { en: 'Automatic checking is not available now — your child rates themselves.', pl: 'Automatyczna ocena jest teraz niedostępna — dziecko ocenia się samo.' } },
};

/** Ostatnia użyta pochwała — kolejne zadania „powiedz” w tej sesji nie powtarzają jej dwa razy z rzędu. */
let lastPraise: FeedbackKey | undefined;
/** Licznik przeplatający kawę i leżak przy spokojnym „dobrze” (wspólny dla wszystkich zdań sesji). */
let relaxedTurn = 0;

const SOURCE_LABEL: Record<AssessmentResult['source'], string> = {
  azure: 'Ocena: Azure (dokładna)',
  transcript: 'Ocena: przeglądarka (uproszczona)',
  self: 'Samoocena',
};

export function renderSpeak(task: SpeakTask, env: TaskEnv): TaskView {
  const { ctx } = env;
  const { player } = ctx;
  const comp = env.companion;
  const words = task.text.trim().split(/\s+/);
  // Scenka (patch-50): rozmowa w dymkach z głowami postaci (src/ui/dialogue.ts), bez maskotki i obrazka; pozostałe fazy mają dotychczasowy układ (zdanie + obrazek / maskotka)
  const isScene = task.phase === 'scene';

  let attempts = 0;
  let bestStars = 0;
  let everPassed = false; // któraś próba zaliczona (werdykt `passed`)
  let attemptId: string | null = null; // zapis ostatniej próby w dzienniku prób (tryb testowy pozwala go opisać)
  let bestPercent: number | undefined; // najlepsze podejście; tylko gdy oceniał Azure (patrz lib/percent.ts) — pokazujemy je dopiero w podsumowaniu
  let busy = false;
  let heardPcm: Int16Array | null = null; // nagranie ostatniej udanej oceny (do zapisu na serwerze, gdy rodzic włączył zgodę)
  let heardRaw: AzureRawPair | null = null; // surowe odpowiedzi Azure tej samej próby (SPEC §23.40) — jadą z nagraniem
  let heardFor: string | null = null; // profil aktywny, gdy dziecko zaczęło mówić (nagranie nie może trafić pod profil przełączony w trakcie oceny)
  let micReady = false; // mikrofon i rozpoznawanie gotowe — dopiero wtedy wolno mówić (inaczej ginie pierwsze słowo)
  const selfOnly = ctx.chain().assessors.length === 0; // brak jakiejkolwiek automatycznej oceny
  let selfOffered = false; // po błędzie oferujemy samoocenę obok przycisku mikrofonu
  let silentTries = 0; // kolejne próby bez wykrytej mowy (nie zużywają podejść, ale nie mogą blokować dziecka w nieskończoność)
  let finished = false;

  let lastResult: AssessmentResult | null = null; // ostatnia próba — karta słowa pokazuje z niej najsłabszą głoskę
  const helpSlot = h('div', { class: 'help-slot' });
  let refit: () => void = () => {}; // scenka: po próbie ekran może zrobić się wyższy (wyjaśnienie problemu, linijki trybu testowego) — skala dopasowuje się od nowa (fitToWindow)
  let helpIndex: number | null = null; // które słowo ma otwartą kartę „jak czytać”
  const markSelected = () => {
    sentence.querySelectorAll<HTMLElement>('.w').forEach((w, i) => w.classList.toggle('sel', i === helpIndex));
  };
  const markHelpOpen = () => el?.classList.toggle('help-open', helpIndex !== null);
  const closeHelp = () => {
    helpIndex = null;
    helpSlot.replaceChildren();
    markSelected();
    markHelpOpen();
  };
  // Kartę można otworzyć także w czasie nagrywania (dziecko czyta z niej, co ma powiedzieć), ale bez dźwięku: odsłuch z głośnika wpadłby do mikrofonu.
  // Po nagraniu karta jest rysowana od nowa (refreshHelp) — z odblokowanym odsłuchem i oceną słów z nowej próby.
  const showHelp = (i: number) => {
    helpIndex = i;
    const scored = lastResult ? (scoresForDisplayWords(task.text, lastResult.words)[i] ?? null) : null;
    const meaning = courseMeaning(ctx.course?.lessons ?? [], words[i] ?? '');
    helpSlot.replaceChildren(renderWordHelp(ctx, words[i] ?? '', scored, player, closeHelp, busy, meaning ? say(ctx, meaning) : null));
    markSelected();
    markHelpOpen();
  };
  const refreshHelp = () => {
    if (helpIndex !== null) showHelp(helpIndex);
  };
  const sentence = h(
    'p',
    { class: isScene ? 'sentence dlg-sentence' : 'sentence big', 'data-testid': 'sentence' },
    ...words.flatMap((w, i): (HTMLElement | string)[] => [
      txAttr(ctx, h('span', { class: 'w', 'data-i': i, 'data-help': '1', role: 'button', tabindex: 0, onclick: () => showHelp(i), onkeydown: (e: Event) => { const k = (e as KeyboardEvent).key; if (k === 'Enter' || k === ' ') { e.preventDefault(); showHelp(i); } } }, w), 'title', T.tapWord),
      ' ',
    ]),
  );
  // tłumaczenie: schowane na starcie, pokazuje je dopiero ikona 文A obok podpisu (patch-31c). Podpowiedzi „Dotknij słowo…” nie ma — słowa mają kropkowane podkreślenie, które samo mówi, że można je dotknąć.
  const pl = h('p', { class: 'pl', 'data-testid': 'pl', hidden: true }, task.pl);
  const feedback = h('div', { class: 'result', 'data-testid': 'feedback', 'aria-live': 'polite' });
  const controls = h('div', { class: 'actions-bar' });
  // Dotknięcie dowolnego miejsca poza kartą „jak czytać” zamyka ją (patch-40), z wyjątkiem samych słów zdania (otwierają/odświeżają kartę), ikony 文A
  // (tłumaczenie ma być widać obok karty), wierszy rozmowy w scence (🔊 i 🐢 przy kwestiach — dziecko czyta radę z karty i słucha) i paska z mikrofonem: dziecko czyta radę z karty i od razu nagrywa albo próbuje jeszcze raz — karta musi zostać na ekranie.
  const offOutside = dismissOnOutsideTap({ isOpen: () => helpIndex !== null, keep: () => [helpSlot, controls, plToggle, ...(dialogue ? [dialogue] : []), ...sentence.querySelectorAll('.w')], close: closeHelp });
  const skipRow = h('div', { class: 'actions skip-row' });
  // prawa strona: obrazek słowa (ten sam w całym przebiegu słowa), po próbie — maskotka z miną zależną od wyniku
  const side = h('div', { class: 'side', 'data-testid': 'speak-side' });
  const pic = isScene ? null : task.image ? lessonPicture(task.image) : h('div', { class: 'mascot-stage' }, mascot(stepPose(task.phase) ?? 'calm'));
  // zdjęcie jest przy zdaniu przed próbą i w czasie nagrywania; po każdej próbie jego miejsce zajmuje maskotka z miną zależną od wyniku (lib/reaction.ts)
  const showSide = (pose: Pose | null, state: 'idle' | 'tried' | 'result') => {
    if (!isScene) side.replaceChildren(pose ? h('div', { class: 'mascot-stage' }, mascot(pose)) : (pic as Node));
    el.dataset.state = state;
  };
  const source = h('p', { class: 'source' });
  const problem = h('p', { class: 'source problem', 'data-testid': 'problem' });
  // Wyjaśnienie pod przyciskami (kłopot z mikrofonem, zapasowa metoda oceny) zostaje na ekranie, więc trzymamy je jako funkcję języka i układamy od nowa po zmianie EN/PL.
  let problemText: ((lang: TeacherLang) => string) | null = null;
  const showProblem = () => {
    problem.textContent = problemText ? problemText(langOf(ctx)) : ''; // język napisów interfejsu (bez `ctx.teacher` polski, jak reszta ekranu)
  };
  const setProblem = (text: ((lang: TeacherLang) => string) | null) => {
    problemText = text;
    showProblem();
    refit();
  };
  const debugLine = h('p', { class: 'source detail', 'data-testid': 'detail' }); // szczegóły oceny (dla rodzica / strojenia)

  const play = (slow: boolean) => {
    comp?.quiet(); // głos bota nie nakłada się na nagranie zdania
    const id = slow && task.slowAudioId ? task.slowAudioId : task.audioId;
    return player.play(id, { text: task.text, lang: 'en', slow });
  };

  // Język uwag bota (przełącznik EN/PL u góry): tekst na ekranie i głos są zawsze w tym samym, wybranym języku — nigdy po trochu w obu.
  const langNow = (): FeedbackLang => ctx.teacher?.lang() ?? config.teacher.defaultLang;
  let shown: { result: AssessmentResult; verdict: Verdict; praise: FeedbackKey } | null = null; // ostatnia ocena na ekranie (do przerysowania po zmianie języka)
  let shownNote: FeedbackKey | null = null; // albo ostatnia krótka uwaga bez oceny (cisza, mikrofon)
  // Uwagi bota pod oceną (patch-31b): TEN SAM tekst, który jest na ekranie, czyta głos ElevenLabs — z nagrań fraz f.<klucz> (po angielsku f.<klucz>.en) i nagrań słów w.<słowo>.
  // Słowa, które wolno mu wymówić, to te z nagraniem: nic, co wymyśli rozpoznawanie, nie trafi do głośnika (lib/feedbackVoice.ts).
  const clipWords = new Set([...planDictionaryWords(), ...planCourseWords(ctx.course?.lessons ?? [])]);
  const hasClip = (w: string) => clipWords.has(wordKey(w));
  const speakClips = (clips: readonly FeedbackClip[]) => {
    if (clips.length === 0) return;
    void player.playAll(clips.map((c) => ({ id: c.id, fallback: { text: c.text, lang: c.lang } })), config.teacher.gapMs);
  };
  /** Uwaga po próbie: wynik, a gdy nie wyszło — także „Usłyszałem: …” (jeśli wszystkie słowa mają nagrania). */
  const feedbackSay = (result: AssessmentResult, verdict: Verdict, praise: FeedbackKey) => {
    const lang = langNow();
    const clips = partsClips(reasonParts(verdict, praise), hasClip, lang);
    if (!verdict.passed && result.source !== 'self' && result.recognizedText) clips.push(...heardClips(verdict.reason === 'unclear' ? 'heardMaybe' : 'heard', result.recognizedText, hasClip, lang));
    speakClips(clips);
  };
  /** Krótka uwaga bez oceny (cisza, kłopot z mikrofonem): tekst w miejscu wyniku i ten sam tekst głosem. */
  const feedbackNote = (key: FeedbackKey) => {
    shown = null;
    shownNote = key;
    const lang = langNow();
    feedback.replaceChildren(h('p', { class: 'result-msg reason', 'data-testid': 'note' }, feedbackText(key, lang)));
    speakClips(partsClips([{ key }], hasClip, lang));
  };

  const renderControls = () => {
    controls.replaceChildren();
    skipRow.replaceChildren();
    for (const r of sceneRows) r.setBusy(busy); // 🔊 i 🐢 w dymkach: w czasie nagrywania wyłączone (głos z głośnika wpadłby do mikrofonu)
    if (isScene) {
      // scenka: odsłuch jest w dymkach, więc na dole zostaje jeden duży przycisk po prawej (jak na makiecie rozmowy): „Mów”, a po zadaniu „Dalej”
      controls.className = finished ? 'actions-bar single end' : selfOnly || selfOffered ? 'actions-bar' : 'actions-bar single end';
      if (finished) controls.append(button([tx(ctx, L.next), icon('arrow')], () => env.done({ taskId: task.id, kind: 'speak', ref: task.sentenceId, phase: task.phase, stars: bestStars, attempts, passed: everPassed, ...(bestPercent !== undefined ? { percent: bestPercent } : {}) }), { class: 'btn primary', testid: 'next' }));
      else {
        if (!selfOnly) controls.append(button(tx(ctx, T.speak), () => void listen(), { class: busy ? (micReady ? 'btn mic listening' : 'btn mic waiting') : 'btn mic', testid: 'mic', disabled: busy }));
        if (selfOnly || selfOffered) {
          controls.append(
            button(tx(ctx, T.selfOk), () => onResult(selfResult(true)), { class: selfOnly ? 'btn primary' : 'btn ghost', testid: 'self-ok', disabled: busy }),
            button(tx(ctx, T.selfRetry), () => onResult(selfResult(false)), { class: 'btn ghost', testid: 'self-retry', disabled: busy }),
          );
        }
      }
      if (!finished && attempts > 0) skipRow.append(button(tx(ctx, T.skip), () => finishWith(), { class: 'btn link', testid: 'skip' }));
      return;
    }
    controls.className = finished ? 'actions-bar two' : 'actions-bar';
    if (finished) {
      controls.append(
        button([icon('speaker'), tx(ctx, L.listen)], () => void play(false), { class: 'btn ghost', testid: 'listen' }),
        button([tx(ctx, L.next), icon('arrow')], () => env.done({ taskId: task.id, kind: 'speak', ref: task.sentenceId, phase: task.phase, stars: bestStars, attempts, passed: everPassed, ...(bestPercent !== undefined ? { percent: bestPercent } : {}) }), { class: 'btn primary', testid: 'next' }),
      );
      return;
    }
    // w czasie nagrywania odsłuch zdania jest wyłączony: głos z głośnika wpadłby do mikrofonu i zostałby oceniony jako mowa dziecka
    controls.append(
      button([icon('speaker'), tx(ctx, L.listen)], () => void play(false), { class: 'btn ghost', testid: 'listen', disabled: busy }),
      button([icon('turtle'), tx(ctx, L.slowly)], () => void play(true), { class: 'btn ghost', testid: 'listen-slow', disabled: busy }),
    );
    if (!selfOnly) {
      controls.append(
        button(tx(ctx, T.speak), () => void listen(), {
          class: busy ? (micReady ? 'btn mic listening' : 'btn mic waiting') : 'btn mic',
          testid: 'mic',
          disabled: busy,
        }),
      );
    }
    if (selfOnly || selfOffered) {
      controls.append(
        button(tx(ctx, T.selfOk), () => onResult(selfResult(true)), { class: selfOnly ? 'btn primary' : 'btn ghost', testid: 'self-ok', disabled: busy }),
        button(tx(ctx, T.selfRetry), () => onResult(selfResult(false)), { class: 'btn ghost', testid: 'self-retry', disabled: busy }),
      );
    }
    if (attempts > 0) skipRow.append(button(tx(ctx, T.skip), () => finishWith(), { class: 'btn link', testid: 'skip' }));
  };

  const finishWith = () => {
    finished = true;
    renderControls();
    refit();
  };

  // Tryb testowy rodzica (Dla rodzica ▸ Dziennik prób): opis prób do strojenia oceny — narzędzie rodzica, dlatego zostaje po polsku (jak sam dziennik prób); dziecko go nie widzi.
  const labelRow = (id: string): HTMLElement => {
    const note = h('span', { class: 'muted', 'data-testid': 'label-note' }, '');
    let current: AttemptLabel | undefined;
    const ok = button('✅ powiedziałem poprawnie', () => choose('ok'), { class: 'btn ghost small', testid: 'label-ok' });
    const bad = button('❌ powiedziałem celowo źle', () => choose('bad'), { class: 'btn ghost small', testid: 'label-bad' });
    const choose = (label: AttemptLabel) => {
      current = current === label ? undefined : label; // drugie kliknięcie zdejmuje opis
      ctx.attempts?.label(id, current);
      ok.className = `btn ghost small${current === 'ok' ? ' on' : ''}`;
      bad.className = `btn ghost small${current === 'bad' ? ' on' : ''}`;
      note.textContent = current ? ` zapisano: ${current === 'ok' ? 'poprawnie' : 'celowo źle'}` : '';
    };
    return h('div', { class: 'label-row', 'data-testid': 'label-row' }, h('span', { class: 'muted' }, 'Tryb testowy — co powiedziałeś? '), ok, bad, note);
  };

  const paint = (result: AssessmentResult, verdict: Verdict, praise: FeedbackKey) => {
    const tones = wordTones(task.text, result.words);
    sentence.querySelectorAll<HTMLElement>('.w').forEach((el, i) => {
      el.className = `w ${tones[i] ?? 'none'}`;
    });
    markSelected();
    const detail =
      result.source === 'azure'
        ? ` · dokładność ${Math.round(result.accuracy)} · płynność ${Math.round(result.fluency ?? 0)}`
        : '';
    // techniczne linijki oceny (metoda, miary, co usłyszano) widzi tylko rodzic w trybie testowym (Dla rodzica ▸ Dziennik prób); dziecko ich nie potrzebuje
    const dev = ctx.attempts?.testMode() === true;
    source.textContent = dev ? SOURCE_LABEL[result.source] + detail : '';
    const note = result.note;
    setProblem(note ? (lang) => backupNote(note, lang) : null);
    debugLine.textContent = dev && result.source !== 'self' ? describeResult(result) : '';
    if (result.note) console.warn('[naative]', result.note);
    const lang = langNow();
    const showHeard = !verdict.passed && result.source !== 'self' && !!result.recognizedText;
    const saidOnly = verdict.reason === 'unclear' && showHeard;
    feedback.replaceChildren(
      starsEl(verdict.stars, 3, 'stars', 'stars', ctx),
      ...(result.source === 'azure' && config.percent.showPerSentence && verdict.reason !== 'unclear' ? [h('p', { class: 'pct-line', 'data-testid': 'sentence-percent', 'data-percent': sentencePercent(result) }, `${sentencePercent(result)}%`)] : []),
      // „Prawie! Nie jestem pewien…”: dłuższą uwagę mówi sam bot (nagranie), na ekranie zostaje linijka „Wydaje mi się, że usłyszałem: …”; tekst nadal jest w drzewie dla czytników ekranu
      h('p', { class: `result-msg reason${saidOnly ? ' sr-only' : ''}`, 'data-testid': 'reason' }, reasonIn(verdict, lang, praise)),
    );
    // tryb testowy (Dla rodzica ▸ Dziennik prób): rodzic opisuje, co naprawdę powiedział — to są dane do strojenia oceny
    if (attemptId && ctx.attempts?.testMode()) feedback.append(labelRow(attemptId));
    // gdy nie wyszło, pokaż, co usłyszał program — dziecko (i rodzic) widzi wtedy, czego brakuje albo co zabrzmiało inaczej
    if (showHeard) {
      feedback.append(h('p', { class: 'heard', 'data-testid': 'heard' }, heardLine(verdict.reason === 'unclear' ? 'heardMaybe' : 'heard', prettyHeard(result.recognizedText ?? ''), lang)));
    }
    refit(); // linijki trybu testowego i „Usłyszałem…” mogą podnieść ekran
  };

  function onResult(result: AssessmentResult): void {
    attempts++;
    el.dataset.attempts = String(attempts);
    const verdict = decide(result, config.scoring);
    lastResult = result;
    attemptId = null;
    if (result.source !== 'self') {
      const rec = makeAttemptRecord({ now: new Date(), seq: attempts, sentenceId: task.sentenceId, reference: task.text, result, verdict });
      ctx.attempts?.record(rec);
      attemptId = rec.id;
      // nagranie głosu do strojenia oceny (SPEC §23.39): tylko kroki 1–2 lekcji i tylko wtedy, gdy rodzic włączył zgodę (sprawdza `ctx.recordings`)
      if (heardPcm && isRecordedPhase(task.phase)) ctx.recordings?.submit({ phase: task.phase, attempt: rec, pcm: heardPcm, ...(heardRaw ? { azure: heardRaw } : {}), profileId: heardFor });
    }
    heardPcm = null;
    heardRaw = null;
    bestStars = Math.max(bestStars, verdict.stars);
    if (verdict.passed) everPassed = true;
    if (result.source === 'azure') bestPercent = Math.max(bestPercent ?? 0, attemptPercent(result, verdict, config));
    // bardzo dobry wynik → jedna z pochwał (Brawo! / Petarda! / Idealnie! / Masz to! / Sztos! / Ogień!), ta sama na ekranie i w głosie, nie dwa razy z rzędu; samoocena zawsze „Brawo!”
    const praise: FeedbackKey = result.source === 'self' ? 'ok' : pickPraise(verdict, lastPraise);
    if (verdict.passed && verdict.stars >= 3 && result.source !== 'self') lastPraise = praise;
    shown = { result, verdict, praise };
    shownNote = null;
    paint(result, verdict, praise);
    const exhausted = !verdict.passed && attempts >= config.scoring.maxAttempts;
    const over = verdict.passed || exhausted; // koniec zadania: maskotka z miną zależną od wyniku; wcześniej zdjęcie zostaje
    // maskotka reaguje po KAŻDEJ próbie (patch-42, progi w config.reaction); stan `tried` = próba za nami, ale jeszcze nie koniec zadania, `result` = koniec
    showSide(reactionPose({ verdict, ...(result.source === 'azure' ? { percent: attemptPercent(result, verdict, config) } : {}), exhausted, self: result.source === 'self', alt: relaxedTurn++ }), over ? 'result' : 'tried');
    busy = false; // przed otwarciem karty słowa: karta rysowana w czasie nagrywania miała zablokowany odsłuch
    // Karta „jak czytać” NIE otwiera się sama po nieudanej próbie (od patcha 49): wcześniej otwierała się przy pierwszym słabym słowie, a gdy źle wypadły dwa słowa, druga
    // połowa problemu nie miała nic — i dziecko dostawało okienko, o które nie prosiło. Słabe słowa są pokolorowane (paint → wordTones) i dotykalne; dziecko otwiera kartę samo.
    // Otwarta karta zostaje (dziecko czyta z niej, co mówić) i jest liczona od nowa z oceny tej próby; po zaliczeniu zdania zamyka się.
    if (verdict.passed) closeHelp();
    else refreshHelp();
    // Uwagę pod oceną czyta bot (nagrania). Gdy AI jest włączone (obecnie wstrzymane), po próbie odpowiada model z wynikiem w ręku;
    // gdy nie — albo gdy zawiedzie — czyta ją bot z nagrań.
    const facts = speakAttempt({ no: attempts, verdict, ...(result.source === 'azure' ? { percent: attemptPercent(result, verdict, config) } : {}), ...(result.recognizedText ? { heard: result.recognizedText } : {}) });
    comp?.taskBusy(false);
    if (comp && result.source !== 'self' && comp.wants(facts)) {
      void comp.react(facts).then((out) => {
        if ((out === 'failed' || out === 'skipped') && !busy) feedbackSay(result, verdict, praise);
      });
    } else feedbackSay(result, verdict, praise);
    if (verdict.passed || exhausted) finished = true;
    renderControls();
  }

  async function listen(): Promise<void> {
    if (busy || comp?.askActive()) return; // dziecko akurat pyta bota — mikrofon jest zajęty
    busy = true;
    comp?.quiet(); // ucisza bota i unieważnia jego spóźnioną reakcję na poprzednią próbę
    comp?.taskBusy(true);
    player.stop();
    feedback.replaceChildren();
    showSide(null, 'idle');
    sentence.querySelectorAll<HTMLElement>('.w').forEach((w) => {
      w.className = 'w';
    });
    markSelected();
    micReady = false;
    setProblem(null);
    debugLine.textContent = '';
    refreshHelp(); // otwarta karta „jak czytać” zostaje (dziecko czyta z niej, co mówić), tylko bez odsłuchu
    renderControls();
    try {
      heardPcm = null;
      heardRaw = null;
      heardFor = ctx.recordings?.current() ?? null;
      const result = await runAssessment(ctx.chain(), task.text, {
        onReady: () => {
          if (!busy || micReady) return;
          micReady = true; // przycisk „Mów” zaczyna pulsować — to znak, że można mówić
          renderControls();
        },
        onRecording: (pcm) => {
          heardPcm = pcm; // dopinamy do próby w `onResult` (ocena wraca zaraz po tym wywołaniu)
        },
        onAzureRaw: (raw) => {
          heardRaw = raw; // jak wyżej: dopinamy do próby w `onResult`
        },
      });
      silentTries = 0;
      onResult(result);
    } catch (e) {
      busy = false;
      refreshHelp();
      comp?.taskBusy(false);
      const code = e instanceof AssessError ? e.code : 'unavailable';
      if (code === 'no-speech') {
        silentTries++;
        if (silentTries >= 3) selfOffered = true; // po trzech próbach bez wykrytej mowy dziecko może ocenić się samo (albo pominąć)
        feedbackNote(silentTries >= 3 ? 'silentMore' : 'silent');
        setProblem(null);
      } else {
        selfOffered = true;
        const causes = e instanceof AssessError ? e.causes : [];
        const key = code === 'unavailable' && causes.includes('network') ? 'network' : code;
        const info = ERROR_FEEDBACK[key] ?? ERROR_FEEDBACK.fallback!;
        feedbackNote(info.say);
        const detail = e instanceof Error ? e.message : String(e);
        setProblem((lang) => `${info.parent[lang]} ${T.technical[lang]}${code}${detail && detail !== code ? ` — ${detail}` : ''}`);
        console.warn('[naative] ocena wymowy nie powiodła się:', e);
      }
      renderControls();
    }
  }

  const plToggle = task.plMode === 'hidden' ? null : h('button', { type: 'button', class: 'icon-sq', 'data-testid': 'show-pl', 'aria-pressed': 'false', onclick: () => {
    pl.hidden = !pl.hidden;
    for (const c of cueBox?.querySelectorAll<HTMLElement>('.cue-pl') ?? []) c.hidden = pl.hidden;
    plToggle?.classList.toggle('on', !pl.hidden);
    plToggle?.setAttribute('aria-pressed', String(!pl.hidden));
  } }, icon('translate'));
  if (plToggle) labelFor(ctx, plToggle, L.polishTranslation);

  // Scenka (kurs v2): kwestię rozmówcy czyta bot jej głosem — na ekranie jest w dymku z głową postaci nad kwestią dziecka; 🔊 i 🐢 w każdym dymku odtwarzają ją normalnie i wolno.
  const cues = task.cue ?? [];
  const cueClips = cues.map((c) => ({ id: c.audioId, fallback: { text: c.text, lang: 'en' as const } }));
  const sceneRows: LineView[] = [];
  const cueRows = cues.map((c) => dialogueLine(ctx, {
    who: c.who,
    body: c.text,
    pl: c.pl,
    testid: 'cue-line',
    textTestid: 'cue-text',
    plTestid: 'cue-pl',
    plClass: 'cue-pl',
    play: { testid: 'cue-play', label: L.listenToSentence, run: () => { comp?.quiet(); return player.play(c.audioId, { text: c.text, lang: 'en' }); } },
    slow: { testid: 'cue-slow', label: L.slowly, run: () => { comp?.quiet(); return player.play(c.slowAudioId ?? c.audioId, { text: c.text, lang: 'en', slow: true }); } },
  }));
  const playCues = () => {
    comp?.quiet();
    if (cueClips.length === 0) return Promise.resolve();
    const all = player.playAll(cueClips, config.teacher.gapMs);
    for (const r of cueRows) r.pulse(all);
    return all;
  };
  const mineRow = isScene ? dialogueLine(ctx, {
    ...(task.role ? { who: task.role } : {}),
    me: true,
    body: sentence,
    pl,
    testid: 'scene-mine',
    play: { testid: 'listen', label: L.listenToSentence, run: () => play(false) },
    slow: { testid: 'listen-slow', label: L.slowly, run: () => play(true) },
  }) : null;
  if (mineRow) sceneRows.push(...cueRows, mineRow);
  const cueBox = cueRows.length === 0 ? null : h('div', { class: 'dlg-cues', 'data-testid': 'cue-box' }, ...cueRows.map((r) => r.el));
  const dialogue = isScene ? h('div', { class: 'dlg', 'data-testid': 'dialogue' }, ...(cueBox ? [cueBox] : []), mineRow!.el) : null;

  const el = h(
    'section',
    { class: 'task speak screen', 'data-task': 'speak', 'data-phase': task.phase, 'data-state': 'idle', 'data-testid': 'task-speak', ...(isScene ? sceneFitProps(task.text.length + Math.max(0, ...(task.cue ?? []).map((c) => c.text.length))) : fitProps(task.text.length)) }, // scenka: kwestia rozmówcy + zdanie dziecka dzielą wysokość
    // Wzorzec ekranu (patch-41): trzy strefy — pasek narzędzi (chip + 文A + „Pomiń”), środek (zdanie z zarezerwowanym miejscem na tłumaczenie i wynik po lewej, obrazek po prawej, w tym samym polu
    // maskotka) i dolny pasek. Karta „jak czytać” jest nakładką nad dolnym paskiem (`help-slot`), więc jej otwarcie niczego nie przesuwa. Nic, co dziecko już widzi, nie zmienia rozmiaru ani miejsca.
    // Scenka (patch-50): w środku dymki rozmowy (kwestia rozmówcy, kwestia dziecka), pod nimi wynik; zamiast tagu roli — „Ty” przy imieniu dziecka.
    h('div', { class: 'tools-row' }, txText(ctx, h('p', { class: 'chip' }), T.phase[task.phase]), plToggle, skipRow), // „Pomiń” po nieudanej próbie: w prawym górnym rogu karty (poza układem), żeby nie dokładać wysokości ani nie przesuwać zdania
    isScene
      ? h('div', { class: 'screen-main scene-main' },
          dialogue,
          problem,
          source,
          debugLine,
        )
      : h('div', { class: 'screen-main' },
          h('div', { class: 'speak-grid' },
            h('div', { class: 'sentence-box' }, h('div', { class: 'sentence-text' }, sentence, pl), h('div', { class: 'result-box' }, feedback)),
            side,
          ),
          problem,
          source,
          debugLine,
        ),
    // scenka: wynik próby leży osobno (nie w środku), tuż nad dolnym paskiem — na tablecie wchodzi w pas obok przycisku „Mów” / „Dalej” (patrz styles.css, „wynik obok przycisku”)
    isScene ? h('div', { class: 'result-box scene-result' }, feedback) : null,
    helpSlot,
    controls,
  );
  const fit = isScene ? fitToWindow(el, { grow: true }) : null;
  if (fit) refit = fit.refit; // scenka: dwie kwestie z głowami dzielą wysokość okna — `--fit` schodzi, aż strona się mieści (jak w „Co powiesz?” i lukach)
  showSide(null, 'idle');
  renderControls();
  if (cues.length > 0) void playCues();
  else if (task.autoPlay) void play(false);
  // zmiana języka uwag bota w trakcie zadania: uwaga pod oceną od razu w nowym języku (tekst i głos), jak w pogadance
  const offLang = ctx.teacher?.onChange(() => {
    showProblem();
    if (busy) return;
    if (shown) {
      player.stop();
      paint(shown.result, shown.verdict, shown.praise);
      feedbackSay(shown.result, shown.verdict, shown.praise);
    } else if (shownNote) {
      player.stop();
      feedbackNote(shownNote);
    }
  });
  return { el, destroy: () => { fit?.dispose(); offOutside(); offLang?.(); comp?.taskBusy(false); player.stop(); } };
}
